// src/hooks/useSubscription.js
import { useState, useEffect } from 'react';
import SubscriptionService from '../services/SubscriptionService';
import { isPaymentEnforcementEnabled } from '../utils/RemoteConfig';

/**
 * Custom hook to manage subscription state
 * Provides real-time subscription status and access control
 * 
 * @param {string} userId - Firebase user ID
 * @returns {Object} Subscription state and helpers
 */
export const useSubscription = (userId) => {
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true); // Default to true to avoid flash
  const [status, setStatus] = useState('loading');
  const [daysLeftInTrial, setDaysLeftInTrial] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [reason, setReason] = useState('');
  const [enforcementEnabled, setEnforcementEnabled] = useState(false);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    console.log('useSubscription - Initializing for user:', userId);

    // Check if enforcement is enabled
    const enabled = isPaymentEnforcementEnabled();
    setEnforcementEnabled(enabled);

    // Initial check
    const checkSubscription = async () => {
      try {
        const result = await SubscriptionService.checkAccess(userId);
        
        setHasAccess(result.hasAccess);
        setStatus(result.status);
        setDaysLeftInTrial(result.daysLeftInTrial);
        setSubscription(result.subscription);
        setReason(result.reason);
        setLoading(false);
        
        console.log('useSubscription - Initial check complete:', result);
      } catch (error) {
        console.error('useSubscription - Error checking subscription:', error);
        // Fail open - grant access on error
        setHasAccess(true);
        setStatus('error');
        setLoading(false);
      }
    };

    checkSubscription();

    // Set up real-time listener for subscription changes
    // Only if payment enforcement is enabled
    let unsubscribe;
    if (enabled) {
      unsubscribe = SubscriptionService.subscribeToSubscription(userId, (subscriptionData) => {
        if (subscriptionData) {
          const { subscription: sub, daysLeftInTrial: days } = subscriptionData;
          
          setSubscription(sub);
          setDaysLeftInTrial(days);
          setStatus(sub.status);
          
          // Update access based on status
          const access = ['trialing', 'active'].includes(sub.status);
          setHasAccess(access);
          
          // Update reason
          let newReason = '';
          if (sub.status === 'trialing') {
            newReason = `Free trial - ${days} days remaining`;
          } else if (sub.status === 'active') {
            newReason = 'Active subscription';
          } else if (sub.status === 'paused') {
            newReason = 'Subscription paused - payment method required';
          } else if (sub.status === 'canceled') {
            newReason = 'Subscription canceled';
          } else if (sub.status === 'past_due') {
            newReason = 'Payment failed - please update payment method';
          }
          setReason(newReason);
          
          console.log('useSubscription - Real-time update:', { status: sub.status, hasAccess: access, days });
        } else {
          // No subscription found
          setSubscription(null);
          setDaysLeftInTrial(null);
          setStatus('none');
          setHasAccess(false);
          setReason('No active subscription or trial');
          
          console.log('useSubscription - No subscription found');
        }
      });
    }

    return () => {
      if (unsubscribe) {
        console.log('useSubscription - Cleaning up listener');
        unsubscribe();
      }
    };
  }, [userId]);

  return {
    loading,
    hasAccess,
    status,
    daysLeftInTrial,
    subscription,
    reason,
    enforcementEnabled,
    
    // Helper methods
    isOnTrial: status === 'trialing',
    isActive: status === 'active',
    isBeta: status === 'beta',
    isPaused: status === 'paused',
    needsPayment: ['paused', 'past_due'].includes(status),
  };
};

export default useSubscription;